import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChatCircle, PaperPlaneRight, User, ArrowLeft, Pencil, Trash, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import MentionTextarea from "../components/MentionTextarea";
import EmojiButton from "../components/EmojiButton";

export default function ForumPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const courseFilter = searchParams.get("course") || "";
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(courseFilter);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [replyContent, setReplyContent] = useState("");

  // Round 35 — forum moderation state
  const isModerator = user && ["super_admin", "admin", "instructor"].includes(user.role);
  const isAdmin = user && ["super_admin", "admin"].includes(user.role);
  const canEdit = (doc) => user && (doc?.author_id === user.id || isModerator);
  const [editingThread, setEditingThread] = useState(false);
  const [editThreadForm, setEditThreadForm] = useState({ title: "", content: "" });
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editReplyContent, setEditReplyContent] = useState("");

  const saveThreadEdit = async () => {
    try {
      await api.put(`/forum/threads/${threadDetail.id}`, editThreadForm);
      toast.success("Thread updated");
      setEditingThread(false);
      const { data } = await api.get(`/forum/${threadDetail.id}`);
      setThreadDetail(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteThread = async () => {
    if (!window.confirm("Delete this thread and all its replies? This cannot be undone.")) return;
    try {
      await api.delete(`/forum/threads/${threadDetail.id}`);
      toast.success("Thread deleted");
      setThreadDetail(null);
      setSelectedThread(null);
      const { data } = await api.get(`/courses/${selectedCourse}/forum`);
      setThreads(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveReplyEdit = async (rid) => {
    try {
      await api.put(`/forum/replies/${rid}`, { content: editReplyContent });
      toast.success("Reply updated");
      setEditingReplyId(null);
      const { data } = await api.get(`/forum/${threadDetail.id}`);
      setThreadDetail(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteReply = async (rid) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(`/forum/replies/${rid}`);
      const { data } = await api.get(`/forum/${threadDetail.id}`);
      setThreadDetail(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleHide = async (rid) => {
    try {
      await api.patch(`/forum/replies/${rid}/hide`);
      const { data } = await api.get(`/forum/${threadDetail.id}`);
      setThreadDetail(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    api.get("/courses").then(r => setCourses(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      setLoading(true);
      api.get(`/courses/${selectedCourse}/forum`).then(r => { setThreads(r.data); setLoading(false); }).catch(() => setLoading(false));
    } else {
      setThreads([]);
      setLoading(false);
    }
  }, [selectedCourse]);

  const loadThread = async (threadId) => {
    setSelectedThread(threadId);
    try {
      const { data } = await api.get(`/forum/${threadId}`);
      setThreadDetail(data);
    } catch { toast.error("Failed to load thread"); }
  };

  const createThread = async () => {
    if (!newTitle.trim() || !selectedCourse) return;
    setPosting(true);
    try {
      await api.post(`/courses/${selectedCourse}/forum`, { title: newTitle, content: newContent });
      toast.success("Discussion started");
      setNewTitle("");
      setNewContent("");
      const { data } = await api.get(`/courses/${selectedCourse}/forum`);
      setThreads(data);
    } catch (e) { toast.error("Failed to create thread"); }
    setPosting(false);
  };

  const postReply = async () => {
    if (!replyContent.trim() || !selectedThread) return;
    setPosting(true);
    try {
      await api.post(`/forum/${selectedThread}/replies`, { content: replyContent });
      setReplyContent("");
      await loadThread(selectedThread);
      toast.success("Reply posted");
    } catch (e) { toast.error("Failed to post reply"); }
    setPosting(false);
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="forum-page">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Community</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Discussion Forum
            </h1>
          </div>
          {selectedCourse && (
            <Link to={`/courses/${selectedCourse}`}>
              <Button variant="outline" className="rounded-none gap-2 text-sm" data-testid="return-to-course-btn">
                <ArrowLeft size={14} /> Return to Course
              </Button>
            </Link>
          )}
        </div>

        <div className="mb-6">
          <Select value={selectedCourse} onValueChange={v => { setSelectedCourse(v); setSelectedThread(null); setThreadDetail(null); }}>
            <SelectTrigger className="w-full max-w-sm rounded-none" data-testid="forum-course-select">
              <SelectValue placeholder="Select a course or community" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alumni">Alumni Community (Programme Graduates)</SelectItem>
              {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {selectedCourse && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Thread List */}
            <div className="lg:col-span-1">
              {/* New Thread */}
              <Card className="border border-slate-200 rounded-none shadow-sm mb-4">
                <CardContent className="p-4">
                  <p className="text-sm font-semibold text-slate-900 mb-2">Start a Discussion</p>
                  <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Topic title" className="mb-2 rounded-none text-sm" data-testid="forum-title-input" />
                  <MentionTextarea value={newContent} onChange={e => setNewContent(e.target.value)} placeholder="What's on your mind? Use @name to tag someone." className="mb-2 text-sm" rows={2} data-testid="forum-content-input" />
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <EmojiButton onSelect={(emoji) => setNewContent(prev => prev + emoji)} testId="forum-thread-emoji" />
                    <Button onClick={createThread} disabled={posting || !newTitle.trim()} size="sm" className="bg-blue-800 text-white hover:bg-blue-900 rounded-none flex-1" data-testid="create-thread-btn">
                      Post Discussion
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-2">
                {loading ? (
                  [1,2,3].map(i => <div key={i} className="h-16 bg-slate-100 animate-pulse" />)
                ) : threads.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No discussions yet. Be the first!</p>
                ) : (
                  threads.map((thread, idx) => (
                    <div
                      key={thread.id}
                      onClick={() => loadThread(thread.id)}
                      data-testid={`forum-thread-${idx}`}
                      className={`border border-slate-200 p-3 cursor-pointer transition-all hover:border-slate-400 ${selectedThread === thread.id ? "border-blue-800 bg-blue-50/50" : "bg-white"}`}
                    >
                      <p className="text-sm font-medium text-slate-900 truncate">{thread.title}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                        <span>{thread.author_name}</span>
                        <span>&middot;</span>
                        <span>{thread.replies_count || 0} replies</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Thread Detail */}
            <div className="lg:col-span-2">
              {threadDetail ? (
                <Card className="border border-slate-200 rounded-none shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      {editingThread ? (
                        <Input value={editThreadForm.title} onChange={e => setEditThreadForm({...editThreadForm, title: e.target.value})} className="rounded-none text-lg font-semibold" data-testid="edit-thread-title" />
                      ) : (
                        <h2 className="text-xl font-semibold text-slate-900 flex-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          {threadDetail.title}
                        </h2>
                      )}
                      {canEdit(threadDetail) && !editingThread && (
                        <div className="flex gap-1">
                          <button onClick={() => { setEditingThread(true); setEditThreadForm({ title: threadDetail.title, content: threadDetail.content }); }} className="p-1 text-slate-400 hover:text-blue-700" data-testid="edit-thread-btn"><Pencil size={14} /></button>
                          <button onClick={deleteThread} className="p-1 text-slate-400 hover:text-red-600" data-testid="delete-thread-btn"><Trash size={14} /></button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-4 text-sm text-slate-500">
                      <User size={14} /> {threadDetail.author_name}
                      <span>&middot;</span>
                      <span>{new Date(threadDetail.created_at).toLocaleDateString()}</span>
                    </div>
                    {editingThread ? (
                      <div className="space-y-2 mb-6">
                        <Textarea rows={4} value={editThreadForm.content} onChange={e => setEditThreadForm({...editThreadForm, content: e.target.value})} className="rounded-none" data-testid="edit-thread-content" />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveThreadEdit} className="bg-blue-800 text-white rounded-none" data-testid="save-thread-edit">Save</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingThread(false)} className="rounded-none">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-slate-700 whitespace-pre-wrap mb-6">{threadDetail.content}</p>
                    )}

                    <Separator className="my-4" />
                    <h3 className="text-base font-semibold text-slate-900 mb-4">
                      Replies ({threadDetail.replies?.length || 0})
                    </h3>

                    <div className="space-y-4 mb-6">
                      {threadDetail.replies?.map((reply, rIdx) => (
                        <div key={reply.id} className="border-l-2 border-slate-200 pl-4" data-testid={`forum-reply-${rIdx}`}>
                          <div className="flex items-center justify-between gap-2 text-xs text-slate-500 mb-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-700">{reply.author_name}</span>
                              <span>{new Date(reply.created_at).toLocaleDateString()}</span>
                              {reply.is_hidden && isAdmin && <Badge className="rounded-none text-[9px] bg-amber-100 text-amber-800">Hidden</Badge>}
                            </div>
                            <div className="flex gap-1">
                              {canEdit(reply) && editingReplyId !== reply.id && (
                                <>
                                  <button onClick={() => { setEditingReplyId(reply.id); setEditReplyContent(reply.content); }} className="p-1 text-slate-400 hover:text-blue-700" data-testid={`edit-reply-${rIdx}`}><Pencil size={12} /></button>
                                  <button onClick={() => deleteReply(reply.id)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`delete-reply-${rIdx}`}><Trash size={12} /></button>
                                </>
                              )}
                              {isAdmin && (
                                <button onClick={() => toggleHide(reply.id)} className="p-1 text-slate-400 hover:text-amber-700" data-testid={`hide-reply-${rIdx}`}>
                                  {reply.is_hidden ? <Eye size={12} /> : <EyeSlash size={12} />}
                                </button>
                              )}
                            </div>
                          </div>
                          {editingReplyId === reply.id ? (
                            <div className="space-y-2">
                              <Textarea rows={3} value={editReplyContent} onChange={e => setEditReplyContent(e.target.value)} className="rounded-none" data-testid={`edit-reply-content-${rIdx}`} />
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => saveReplyEdit(reply.id)} className="bg-blue-800 text-white rounded-none" data-testid={`save-reply-${rIdx}`}>Save</Button>
                                <Button size="sm" variant="outline" onClick={() => setEditingReplyId(null)} className="rounded-none">Cancel</Button>
                              </div>
                            </div>
                          ) : (
                            <p className={`text-sm ${reply.is_hidden ? "italic text-slate-400" : "text-slate-700"}`}>{reply.content}</p>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <MentionTextarea
                          value={replyContent}
                          onChange={e => setReplyContent(e.target.value)}
                          placeholder="Write a reply… Use @name to tag someone."
                          rows={2}
                          data-testid="forum-reply-input"
                        />
                        <div className="absolute bottom-1 left-1">
                          <EmojiButton onSelect={(emoji) => setReplyContent(prev => prev + emoji)} testId="forum-reply-emoji" />
                        </div>
                      </div>
                      <Button onClick={postReply} disabled={posting || !replyContent.trim()} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none self-end" data-testid="post-reply-btn">
                        <PaperPlaneRight size={16} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <ChatCircle size={48} className="mb-3" />
                  <p className="text-sm">Select a discussion to view</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
